import { IconDental } from '@tabler/icons-react';
import { BenefitType, SupplementalPlanForEnrollment } from '@zorro/clients';
import {
  MonolithMethodNames,
  UseMonolithQueryProps,
  useMonolithQuery,
} from '@zorro/web/ui-utils';

import { useEnrollmentAllowanceStore } from '../../EnrollmentAllowanceCard';
import { FullPageLoader } from '../../FullPageLoader';
import {
  PlanElectionForm,
  mapPlanElectionItemToEnrollmentAllowanceStorePlan,
  usePlanElectionForm,
} from '../../PlanElectionForm';
import {
  ManualSubmissionStep,
  ManualSubmissionStepSubmit,
  ManualSubmissionStepType,
} from '../ManualSubmissionStep';
import {
  exportSupplementalPlanList,
  getDefaultSupplementalPlanFormFields,
  getEmptySupplementalPlanFormFields,
  mapSupplementalPlanToPlanElectionItem,
} from '../ManualSubmissionSupplementalPlanStepUtils';
import { useBenefitAddToCart } from '../useBenefitAddToCart';

const benefitType = BenefitType.SUPPLEMENTAL_DENTAL;
const dentalPlanMethod: MonolithMethodNames = 'dentalControllerSearch';

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  onSubmit: () => void;
  isDon?: boolean;
};

export const ManualSubmissionDentalPlanStep = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  onSubmit: onSubmitProp,
  isDon = false,
}: Props) => {
  const setDentalPlans = useEnrollmentAllowanceStore(
    (state) => state.setDentalPlans
  );
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });

  const dentalBenefits = benefits?.dentalBenefits;
  const { submitAddToCart } = useBenefitAddToCart({
    benefitType,
    onboardingPeriodId,
  });

  const {
    isSubmitting,
    planElectionForm,
    submittedWhoIsEnrolledOptions,
    whoIsEnrolledInMultiplePlans,
    insured,
    insuredIdToNameMap,
    onSubmit,
  } = usePlanElectionForm({
    employeeId,
    onboardingPeriodId,
    onSubmit: submitAddToCart,
    onPlansChange: (plans) => {
      const storePlans =
        mapPlanElectionItemToEnrollmentAllowanceStorePlan(plans);
      setDentalPlans(storePlans);
    },
    getDefaultFormFields: (allWhoIsEnrolled: string[] = []) => {
      return getDefaultSupplementalPlanFormFields(
        dentalBenefits,
        allWhoIsEnrolled
      );
    },
  });

  if (!insured) {
    return <FullPageLoader />;
  }

  const handleStepSubmit: ManualSubmissionStepSubmit = async () => {
    if (await planElectionForm.trigger()) {
      onSubmitProp();
      return { navigate: await onSubmit() };
    }

    return { navigate: false };
  };

  const handleGetParams = (
    whoIsEnrolled: string[]
  ): UseMonolithQueryProps<typeof dentalPlanMethod>['params'] => {
    return [onboardingPeriodId, { insuredIds: whoIsEnrolled }];
  };

  const handleDownloadPlans = (
    dentalPlans: SupplementalPlanForEnrollment[] | undefined
  ) => {
    if (dentalPlans) {
      exportSupplementalPlanList(
        dentalPlans,
        `${insured.employee.firstName} ${insured.employee.lastName}`
      );
    }
  };

  return (
    <ManualSubmissionStep
      step={ManualSubmissionStepType.DENTAL}
      submitLabel="Continue"
      onboardingPeriodId={onboardingPeriodId}
      onSubmit={handleStepSubmit}
      isSubmitDisabled={isSubmitting}
      employerId={employerId}
      employeeId={employeeId}
      isDon={isDon}
      withReviewCard
    >
      <PlanElectionForm
        getDefaultPlanElectionFormFields={() =>
          getEmptySupplementalPlanFormFields(submittedWhoIsEnrolledOptions)
        }
        mapDataToPlanElectionItem={mapSupplementalPlanToPlanElectionItem}
        whoIsEnrolledOptions={submittedWhoIsEnrolledOptions}
        whoIsEnrolledInMultiplePlans={whoIsEnrolledInMultiplePlans}
        addPlanButtonText="Add another dental plan"
        insuredIdToNameMap={insuredIdToNameMap}
        onDownloadPlans={handleDownloadPlans}
        planElectionForm={planElectionForm}
        getParams={handleGetParams}
        method={dentalPlanMethod}
        benefitType={benefitType}
        icon={IconDental}
        title="Dental plan"
      />
    </ManualSubmissionStep>
  );
};
